import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { BRAND, BRAND_DARK, BRAND_LIGHT, RED} from "@/shared/constants.js";
import { STEP_PATHS } from "@/modules/Quote/constants.js";
import { fmtDate, formatDate, toMdY, yearsBetweenMdY } from "@/shared/utils/dateHelpers.js";
import { ZIPCODE_NOT_AVAILABLE_MSG, toError } from "@/shared/utils/misc.js";
import {
  deriveHoursFactorFromIlf,
  getDefaultHoursWorkedRow,
  ilfDlfHasRequiredDefaults,
  resolveOrderEffectiveDate,
  resolveOrderExpirationDate,
  resolveSpecialityId,
} from "@/modules/Quote/utils/ilfHelpers.js";
import {
  buildSubmissionRequest,
  submissionIdFromSignupResponse,
} from "@/modules/Quote/utils/submission.js";
import { postInsuredSignup } from "@/modules/Auth/api/authApi.js";
import { putInsuredWithLocation } from "@/modules/Profile/api/profileApi.js";
import {
  buildSingleGroupSavePayload,
  findSubmissionGroup,
  hydrateSubmissionAnswers,
  isQuestionVisible,
  mergeAnswerMaps,
  recomputeHiddenQuestionIds,
  requiredQuestionsAnswered,
  saveSubmissionQuestionAnswers,
  sortedOptions,
  sortedQuestions,
} from "@/modules/Quote/api/questionsApi.js";
import { getZipcodeData, putUpdateRating } from "@/modules/Quote/api/ratingApi.js";
import { refreshPaymentOrder } from "@/modules/Payment/services/paymentOrderService.js";
import { isValidLicenseNumber, isValidMdyDate, isValidNpi } from "@/shared/utils/validators.js";

// DOB is optional, but if provided it must be a real-looking MM/DD/YYYY
// in the past. Spec marks the field as date-time on the server; we ship
// the user's local MM/DD/YYYY string and let the server normalize.
const isDobAcceptable = (raw) => {
  const s = String(raw || "").trim();
  if (s === "") return true;
  if (!isValidMdyDate(s)) return false;
  const [m, d, y] = s.split("/").map((n) => Number(n));
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return false;
  // Reject obvious future dates (per spec: "Future dates are not allowed.").
  return dt.getTime() <= Date.now();
};

import { Field } from "@/shared/components/Field.jsx";
import { TextInput } from "@/shared/components/TextInput.jsx";
import { DateInput } from "@/shared/components/DateInput.jsx";
import { RadioCard } from "@/shared/components/RadioCard.jsx";
import { Toggle } from "@/shared/components/Toggle.jsx";
import { QuoteStrip } from "@/modules/Quote/components/QuoteStrip.jsx";
import { Spacer } from "@/shared/components/Spacer.jsx";
import { PencilIcon, CheckIcon } from "@/shared/components/Icon.jsx";
import Alert from "@/shared/components/Alert.jsx";
import Loader from "@/shared/components/Loader.jsx";
import { btnPrimary, dis } from "@/shared/utils/styles.js";
import {
  resetIlfDlfError,
  useIlfDlfError,
  useIlfDlfFetcher,
  useIlfDlfResponse,
} from "@/hooks/useIlfDlfFetcher.js";
import { useSpeciality } from "@/hooks/useSpeciality.js";

import { useStore } from "@/shared/store/useStore.js";
import personalStore from "@/modules/Quote/store/personalStore.js";
import questionsStore from "@/modules/Quote/store/questionsStore.js";
import quoteFormStore from "@/modules/Quote/store/quoteFormStore.js";
import classificationStore from "@/modules/Quote/store/classificationStore.js";
import coverageStore from "@/modules/Quote/store/coverageStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";
import signupStore from "@/modules/Auth/store/signupStore.js";
import insuredProfileStore from "@/shared/store/insuredProfileStore.js";
import sessionStore from "@/shared/store/sessionStore.js";
import ilfDlfStore from "@/modules/Quote/store/ilfDlfStore.js";
import specialityStore from "@/modules/Quote/store/specialityStore.js";
import paymentOrderStore from "@/modules/Payment/store/paymentOrderStore.js";
import { useQuoteSnapshot } from "@/modules/Quote/utils/useQuoteSnapshot.js";

export default function LicenseScopePage() {
  const navigate = useNavigate();
  const [isEditingPolicyHolderName, setIsEditingPolicyHolderName] = useState(false);
  // Flipped true the first time the user edits the practice ZIP on this
  // page. Drives the speciality source for the save-time PUT /rating:
  //   - ZIP changed → speciality = ilfDlfResponse.defaultSurgery (fresh
  //     from the new /auth/quotedata response)
  //   - ZIP unchanged → speciality = ratingResponse.surgeryId (the value
  //     already on the existing rating row)
  const zipChangedRef = useRef(false);

  // ── Store reads ─────────────────────────────────────────────────────────
  const firstName        = useStore(personalStore, (s) => s.firstName);
  const lastName         = useStore(personalStore, (s) => s.lastName);
  const dob              = useStore(personalStore, (s) => s.dob);
  const licenseNum       = useStore(personalStore, (s) => s.licenseNum);
  const npi              = useStore(personalStore, (s) => s.npi);
  const practiceAddress1 = useStore(personalStore, (s) => s.practiceAddress1);
  const practiceAddress2 = useStore(personalStore, (s) => s.practiceAddress2);
  const practiceCity     = useStore(personalStore, (s) => s.practiceCity);
  const practiceState    = useStore(personalStore, (s) => s.practiceState);
  const practiceZip      = useStore(personalStore, (s) => s.practiceZip);

  const questionAnswers         = useStore(questionsStore, (s) => s.questionAnswers);
  const hiddenQuestionIds       = useStore(questionsStore, (s) => s.hiddenQuestionIds);
  const submissionQuestionGroups = useStore(questionsStore, (s) => s.submissionQuestionGroups);
  const submissionQuestionsLoading = useStore(questionsStore, (s) => s.submissionQuestionsLoading);
  const submissionQuestionsError   = useStore(questionsStore, (s) => s.submissionQuestionsError);

  const zip       = useStore(quoteFormStore, (s) => s.zip);
  const effDate   = useStore(quoteFormStore, (s) => s.effDate);

  const retroDate = useStore(coverageStore, (s) => s.retroDate);

  const flowSubmissionId      = useStore(submissionStore, (s) => s.flowSubmissionId);
  const paymentOrderDetails   = useStore(paymentOrderStore, (s) => s.paymentOrderDetails);
  const insuredProfile        = useStore(insuredProfileStore, (s) => s.insuredProfile);
  const isAuthenticated       = Boolean(insuredProfile?.id || insuredProfile?.name || insuredProfile?.username);

  const regFirstName = useStore(signupStore, (s) => s.regFirstName);
  const regLastName  = useStore(signupStore, (s) => s.regLastName);
  const regEmail     = useStore(signupStore, (s) => s.regEmail);
  const regPhone     = useStore(signupStore, (s) => s.regPhone);
  const regPassword  = useStore(signupStore, (s) => s.regPassword);
  const regStreet    = useStore(signupStore, (s) => s.regStreet);
  const regCity      = useStore(signupStore, (s) => s.regCity);
  const regState     = useStore(signupStore, (s) => s.regState);
  const signupError  = useStore(signupStore, (s) => s.signupError);
  const signupLoading = useStore(signupStore, (s) => s.signupLoading);
  const signupDone    = useStore(signupStore, (s) => s.signupDone);

  const ilfDlfResponse   = useIlfDlfResponse();
  const ilfDlfError      = useIlfDlfError();
  const specialityMaster = useSpeciality();
  const fetchIlfDlf      = useIlfDlfFetcher();
  const { quoteStripPremium, quoteStripAnnualTotal, snapshotLimits, snapshotPolicyLine, quoteStripAmountPending, snapshotExpMdY } = useQuoteSnapshot();

  // The submission-scoped tree (loaded by useAppBootstrap effect #5) is
  // the single source of truth for License/Scope/Practice — questions,
  // saved answers, and ids all come from `/questions/{submissionId}`.
  const lspGroup = findSubmissionGroup(submissionQuestionGroups, /license.*scope.*practice/i);

  // ── Clear any ILF/DLF error carried over from the estimate page ───────
  // `ilfDlfError` lives in a shared store, so a "zip not available" message
  // set on /location follows the user here and would both render an alert
  // and block Continue. It's only meaningful on this page once the user
  // edits the practice zip (which refetches and sets a fresh error), so we
  // drop the stale one on mount. The Continue path still re-validates the
  // ILF/DLF response, so this can't mask a genuinely unratable zip.
  useEffect(() => {
    resetIlfDlfError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Browser Back → dashboard ──────────────────────────────────────────
  // Both the classification and registration steps PUSH /license onto the
  // history stack, so the native browser Back button would otherwise return
  // to whichever page the user came from. Per spec, Back from License must
  // ALWAYS land on the dashboard — matching the in-app back arrow (FlowLayout
  // goBack, step 4). The in-app arrow already does this; this handles the
  // browser/hardware Back button too. We seed one sentinel history entry on
  // mount; the first Back press pops it (the URL is still /license, so React
  // Router doesn't change routes) and our popstate handler redirects to the
  // dashboard, keeping flowSubmissionId so the order stays resumable.
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const onPopState = () => {
      sessionStore.dashView = "dashboard";
      navigate("/dashboard", { replace: true });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [navigate]);

  // ── Fetch the order on mount, then auto-fill any empty fields from it ──
  // The submission already exists at this point (signup or auth'd /submission
  // created it), so GET /insured/order tells us what the backend has on file.
  // `ifMissing` keeps Resume → mount from double-firing: if the dashboard
  // Resume handler just loaded this sid, we reuse the store value.
  useEffect(() => {
    if (!flowSubmissionId) return;
    refreshPaymentOrder(flowSubmissionId, { ifMissing: true });
  }, [flowSubmissionId]);

  // Recompute the submission-wide hidden set from the whole tree + all live
  // answer maps. Reads directly off the store so it's correct even when called
  // synchronously right after an answer write.
  const recomputeHidden = () => {
    const liveAnswers = mergeAnswerMaps(
      questionsStore.questionAnswers,
      questionsStore.coverageAnswers,
      questionsStore.underwritingAnswers,
    );
    questionsStore.setHiddenQuestionIds(
      recomputeHiddenQuestionIds(
        questionsStore.submissionQuestionGroups,
        liveAnswers,
        questionsStore.showTargetQuestionIds,
      ),
    );
  };

  // ── Hydrate saved LSP answers from the submission tree ────────────────
  // The submission API ships each option with its saved `answerValue`.
  // We key answers by submission question id (same id the page uses for
  // setRadio/toggleCheckbox/setText), so render + save both line up.
  // Merge UNDER current store values so in-progress edits aren't lost.
  useEffect(() => {
    if (!lspGroup) return;
    const { answers, impacts } = hydrateSubmissionAnswers(lspGroup);
    if (Object.keys(answers).length > 0) {
      questionsStore.questionAnswers = { ...answers, ...questionsStore.questionAnswers };
    }
    if (Object.keys(impacts).length > 0) {
      questionsStore.impactAnswers = { ...impacts, ...questionsStore.impactAnswers };
    }
    recomputeHidden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lspGroup]);

  useEffect(() => {
    const od = paymentOrderDetails;
    if (!od) return;
    // Prefer the API value, but only when the corresponding store field
    // is still empty — never clobber what the user has typed.
    if (!personalStore.firstName && od.insuredfirstname) personalStore.firstName = od.insuredfirstname;
    if (!personalStore.lastName  && od.insuredlastname)  personalStore.lastName  = od.insuredlastname;
    if (!personalStore.dob       && od.insureddob)       personalStore.dob       = formatDate(toMdY(od.insureddob));
    if (!personalStore.licenseNum && od.licenseNumber)   personalStore.licenseNum = od.licenseNumber;
    if (!personalStore.npi       && od.npiNumber)        personalStore.npi       = od.npiNumber;

    const loc = od.locationResponse;
    if (loc) {
      if (!personalStore.practiceAddress1 && loc.address1) personalStore.practiceAddress1 = loc.address1;
      if (!personalStore.practiceAddress2 && loc.address2) personalStore.practiceAddress2 = loc.address2;
    }
    const r = od.ratingResponse;
    if (r) {
      if (r.practiceCity)     personalStore.practiceCity  = r.practiceCity;
      if (r.practiceLocation) personalStore.practiceState = String(r.practiceLocation).slice(0, 2).toUpperCase();
      if (r.practicezipcode)  personalStore.practiceZip   = r.practicezipcode;
    }
    const c = od.contactResponse;
    if (c?.contactnumber && !signupStore.regPhone) signupStore.regPhone = c.contactnumber;
  }, [paymentOrderDetails]);

  // ── Reload guard ────────────────────────────────────────────────────────
  // On reload the in-memory stores are empty (no `flowSubmissionId`), so
  // there's no submission to render. Send the user to /dashboard — that
  // page will either render their submissions list (if authenticated) or
  // bounce to /signin (if not). Placed AFTER all hooks so React's
  // rules-of-hooks stay satisfied.
  if (!flowSubmissionId) {
    return <Navigate to="/dashboard" replace />;
  }

  // ── canAdvance (step 4) ─────────────────────────────────────────────────
  // Spec-mirrored validation. Patterns match `weborder.json` →
  // InsuredRequest.{npiNumber, licenseNumber} so a failed gate here matches
  // what the server's 400 reason would be.
  const canAdvance = () => {
    if (firstName.trim().length < 1 || lastName.trim().length < 1) return false;
    if (!isValidNpi(npi.replace(/\D/g, ""))) return false;
    if (!isValidLicenseNumber(licenseNum.trim())) return false;
    if (!isDobAcceptable(dob)) return false;
    if (practiceAddress1.trim().length < 2) return false;
    if (practiceCity.trim().length < 2) return false;
    if (practiceState.trim().length < 2) return false;
    if (practiceZip.replace(/\D/g, "").length !== 5) return false;
    return requiredQuestionsAnswered(lspGroup?.questions, questionAnswers, {
      isVisible: (q) => isQuestionVisible(q, hiddenQuestionIds),
    });
  };

  // ── Continue: save (authed) OR signup (anonymous) ─────────────────────
  const handleContinue = async () => {
    if (!firstName || !lastName || signupLoading) return;
    if (isAuthenticated || signupDone) await saveAuthedAndAdvance();
    else                                await signupAnonymousAndAdvance();
  };

  const saveAuthedAndAdvance = async () => {
    if (!flowSubmissionId) { submissionStore.step = 4; navigate(STEP_PATHS[4]); return; }
    if (!lspGroup) { submissionStore.step = 4; navigate(STEP_PATHS[4]); return; }
    if (ilfDlfError) { signupStore.signupError = ilfDlfError; return; }

    signupStore.signupError = null;
    signupStore.signupLoading = true;
    try {
      await maybeUpdateRating();
      // Continue on this page must POST /questions/save every time, whether or
      // not the user provided any answer (§6.8). When there's nothing to
      // persist, buildSingleGroupSavePayload returns null, so fall back to an
      // empty-questions payload for the LSP group and save unconditionally.
      const payload = buildSingleGroupSavePayload(
        /license.*scope.*practice/i,
        flowSubmissionId,
        submissionQuestionGroups,
        questionAnswers,
        questionsStore.impactAnswers,
        hiddenQuestionIds,
      ) || {
        submissionId: Number(flowSubmissionId),
        groups: [{ submissionQuestionGroupId: lspGroup.id, questions: [] }],
      };
      await saveSubmissionQuestionAnswers(payload);
      await maybeUpdateInsuredWithLocation();
      await refreshPaymentOrder(flowSubmissionId);
      submissionStore.step = 4;
      navigate(STEP_PATHS[4]);
    } catch (e) {
      signupStore.signupError = toError(e).message.replace(/^Error:\s*/i, "") || "Could not save answers.";
    } finally {
      signupStore.signupLoading = false;
    }
  };

  const maybeUpdateRating = async () => {
    const cleanZip = practiceZip.replace(/\D/g, "").slice(0, 5);
    const r = paymentOrderDetails?.ratingResponse;
    if (cleanZip.length !== 5 || !r?.id) return;

    const effMdY = toMdY(effDate) || toMdY(resolveOrderEffectiveDate(paymentOrderDetails));
    const apiRetro = r?.retroCoverageDate ?? r?.retrocoveragedate ?? "";
    const retroMdY = toMdY(retroDate) || toMdY(apiRetro);
    const calcYear = retroMdY && effMdY ? yearsBetweenMdY(retroMdY, effMdY) : 1;
    const expMdY = toMdY(resolveOrderExpirationDate(paymentOrderDetails)) || toMdY(snapshotExpMdY) || "";
    // ZIP edited on this page → take the new defaults from QuoteFormData
    // (defaultSurgery). Otherwise stick with the existing rating row's
    // surgeryId so an untouched order doesn't get re-specialized.
    const specialityId = zipChangedRef.current
      ? (Number(ilfDlfResponse?.defaultSurgery)
        || Number(r?.surgeryId)
        || resolveSpecialityId({ paymentOrderDetails, ilfDlfResponse, specialityMaster }))
      : (Number(r?.surgeryId)
        || Number(ilfDlfResponse?.defaultSurgery)
        || resolveSpecialityId({ paymentOrderDetails, ilfDlfResponse, specialityMaster }));
    
        const coverageilfdlfid = zipChangedRef.current
          ? (Number(ilfDlfResponse?.defaultIlfDlf) || undefined)
          : (Number(r?.coverageLimitId) || Number(ilfDlfResponse?.defaultIlfDlf) || undefined);
        
        const retroilfdlfid = zipChangedRef.current
          ? (Number(ilfDlfResponse?.defaultIlfDlf) || undefined)
          : (Number(r?.retroLimitId) || Number(ilfDlfResponse?.defaultIlfDlf) || undefined);

    const putBody = {
      zipcode: cleanZip,
      specialityDetailsId: specialityId,
      year: calcYear,
      coverageilfdlfid,
      retroilfdlfid,
      hoursperweek: String(
        ilfDlfResponse?.defaultHoursWorkedName
        ?? r?.hoursPerWeekLookName
        ?? ilfDlfResponse?.defaultParttimeFulltimeFactor
        ?? getDefaultHoursWorkedRow(ilfDlfResponse)?.id
        ?? "",
      ),
      parttimeFulltimeFactor: r?.parttimeFulltimeFactor ?? deriveHoursFactorFromIlf(ilfDlfResponse),
      insuredId: insuredProfile?.id ? Number(insuredProfile.id) : undefined,
      submissionId: String(flowSubmissionId),
      singleRatingId: Number(r.id),
      effectiveDate: effMdY,
      retroCoverageDate: retroMdY || effMdY,
      claims: String(r?.claims ?? ilfDlfResponse?.defaultClaims ?? 1),
    };
    if (expMdY) putBody.expiredDate = expMdY;
    await putUpdateRating(putBody);
  };

  const maybeUpdateInsuredWithLocation = async () => {
    const primaryLoc = Array.isArray(paymentOrderDetails?.planLocatoins)
      ? paymentOrderDetails.planLocatoins.find((l) => l?.isprimary === true)
      : null;
    const locationId = primaryLoc?.id ?? primaryLoc?.locationId
      ?? paymentOrderDetails?.locationResponse?.locationId;
    const contactId = paymentOrderDetails?.contactResponse?.id;
    const contactnumber = (paymentOrderDetails?.contactResponse?.contactnumber || regPhone || "")
      .toString().replace(/\D/g, "");
    if (locationId == null || contactId == null || contactnumber.length < 10) return;

    await putInsuredWithLocation({
      firstname: firstName,
      lastname: lastName,
      insuredtype: "person",
      contactnumber: contactnumber.slice(0, 15),
      address1: practiceAddress1.trim(),
      address2: practiceAddress2.trim(),
      city: practiceCity.trim(),
      state: practiceState.trim().slice(0, 2).toUpperCase(),
      zipcode: practiceZip.replace(/\D/g, "").slice(0, 5),
      insureddob: dob.replace(/\s/g, ""),
      licenseNumber: licenseNum.trim(),
      npiNumber: npi.replace(/\D/g, "").slice(0, 10),
      isupdatemaster: false,
      locationId: Number(locationId),
      contactId: Number(contactId),
      submissionId: Number(flowSubmissionId),
    });
  };

  const signupAnonymousAndAdvance = async () => {
    signupStore.signupError = null;
    signupStore.signupLoading = true;
    try {
      if (!ilfDlfHasRequiredDefaults(ilfDlfResponse)) throw new Error(ZIPCODE_NOT_AVAILABLE_MSG);
      const signupPayload = {
        isEntity: false,
        firstName: regFirstName,
        lastName: regLastName,
        zipCode: zip,
        address1: practiceAddress1 || regStreet,
        address2: practiceAddress2 || "",
        city: practiceCity.trim() || regCity.trim() || ilfDlfResponse?.city || "",
        state: (practiceState.trim() || regState.trim() || ilfDlfResponse?.st || ilfDlfResponse?.state || "")
          .slice(0, 2).toUpperCase(),
        email: regEmail,
        contactNumber: regPhone,
        password: regPassword,
        title: "Primary location",
        submissionRequest: buildSubmissionRequest({
          zip, effDate, ilfDlfResponse,
          visitedQuestionGroups: [], questionGroupsWithIds: [],
          questionAnswers: {}, impactAnswers: {},
        }),
      };
      const resp = await postInsuredSignup(signupPayload);
      signupStore.registered = true;
      signupStore.signupDone = true;
      const sid = submissionIdFromSignupResponse(resp);
      if (sid) submissionStore.flowSubmissionId = sid;
      if (!insuredProfileStore.insuredProfile) {
        insuredProfileStore.insuredProfile = {
          id: resp?.id, email: regEmail, username: regEmail, abbreviation: resp?.abbreviation,
        };
      }
      submissionStore.step = 4;
      navigate(STEP_PATHS[4]);
    } catch (e) {
      signupStore.signupError = toError(e).message.replace(/^Error:\s*/i, "");
    } finally {
      signupStore.signupLoading = false;
    }
  };

  // ── ZIP change: refetch ILF/DLF + auto-fill city/state ─────────────────
  // `useIlfDlfFetcher` guarantees the speciality id is the master id from
  // /auth/speciality/SP_203 (fetching it first if not cached), so we just
  // hand off zip + effective date and let the hook own the rest.
  const fireZipUpdateRating = async (newZip) => {
    const effMdY = toMdY(effDate) || toMdY(resolveOrderEffectiveDate(paymentOrderDetails));
    try {
      const zipData = await getZipcodeData(newZip);
      const zipCity = zipData?.city != null ? String(zipData.city).trim() : "";
      const zipSt = (zipData?.st ?? zipData?.state ?? "").toString().trim().slice(0, 2).toUpperCase();
      if (zipCity) personalStore.practiceCity = zipCity;
      if (zipSt) personalStore.practiceState = zipSt;
    } catch (_) { /* zip lookup is best-effort */ }
    try {
      await fetchIlfDlf({
        zipcode: newZip,
        ...(effMdY ? { effectiveDate: effMdY } : {}),
      });
    } catch (err) {
      ilfDlfStore.ilfDlfError = err?.message || ZIPCODE_NOT_AVAILABLE_MSG;
    }
  };

  // ── Save name on inline edit close ─────────────────────────────────────
  const saveNameEdit = async () => {
    const primaryLoc = Array.isArray(paymentOrderDetails?.planLocatoins)
      ? paymentOrderDetails.planLocatoins.find((l) => l?.isprimary === true)
      : null;
    const locationId = primaryLoc?.id ?? primaryLoc?.locationId ?? paymentOrderDetails?.locationResponse?.locationId;
    const contactId = paymentOrderDetails?.contactResponse?.id;
    const contactnumber = (paymentOrderDetails?.contactResponse?.contactnumber || regPhone || "").toString().replace(/\D/g, "");
    if (locationId == null || contactId == null || contactnumber.length < 10 || !flowSubmissionId) return;
    personalStore.nameSaveError = null;
    personalStore.nameSaving = true;
    try {
      await putInsuredWithLocation({
        firstname: firstName,
        lastname: lastName,
        insuredtype: "person",
        contactnumber: contactnumber.slice(0, 15),
        address1: practiceAddress1.trim(),
        address2: practiceAddress2.trim(),
        city: practiceCity.trim(),
        state: practiceState.trim().slice(0, 2).toUpperCase(),
        zipcode: practiceZip.replace(/\D/g, "").slice(0, 5),
        insureddob: dob.replace(/\s/g, ""),
        licenseNumber: licenseNum.trim(),
        npiNumber: npi.replace(/\D/g, "").slice(0, 10),
        isupdatemaster: false,
        locationId: Number(locationId),
        contactId: Number(contactId),
        submissionId: Number(flowSubmissionId),
      });
      await refreshPaymentOrder(flowSubmissionId);
    } catch (err) {
      personalStore.nameSaveError = toError(err).message.replace(/^Error:\s*/i, "") || "Could not save name.";
    } finally {
      personalStore.nameSaving = false;
    }
  };

  // ── Question setters ────────────────────────────────────────────────────
  const setRadio = (q, optionId) => {
    questionsStore.questionAnswers = { ...questionAnswers, [String(q.id)]: String(optionId) };
    recomputeHidden();
  };
  const toggleCheckbox = (q, optionId) => {
    const qid = String(q.id);
    const cur = Array.isArray(questionAnswers[qid]) ? questionAnswers[qid] : [];
    const oid = String(optionId);
    const next = cur.includes(oid) ? cur.filter((x) => x !== oid) : [...cur, oid];
    questionsStore.questionAnswers = { ...questionAnswers, [qid]: next };
    recomputeHidden();
  };
  const setText = (q, value) => {
    questionsStore.questionAnswers = { ...questionAnswers, [String(q.id)]: value };
  };

  // ── Render helpers ─────────────────────────────────────────────────────
  const isChip = (q) =>
    q?.questionType === "CHECKBOX" && Array.isArray(q.options) && q.options.length > 4;

  const requiredMark = <span style={{ color: RED, marginLeft: 4 }}>*</span>;
  const withRequired = (text) => <>{text}{requiredMark}</>;

  const renderQuestion = (q) => {
    if (!isQuestionVisible(q, hiddenQuestionIds)) return null;

    const qid = String(q.id);
    const opts = sortedOptions(q.options);
    const required = q.isRequired ? <span style={{ color: RED, marginLeft: 4 }}>*</span> : null;

    return (
      <div key={qid} style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 500, color: "#1a1a1a", fontFamily: "'Fraunces', serif", marginBottom: 10 }}>
          {q.questionText}{required}
        </div>

        {q.questionType === "RADIO_BUTTON" && opts.map((o) => (
          <RadioCard
            key={o.id}
            selected={String(questionAnswers[qid]) === String(o.id)}
            onClick={() => setRadio(q, o.id)}
            title={o.optionLabel}
            subtitle={o.optionDescription || o.description || null}
          />
        ))}

        {q.questionType === "YES_NO" && (
          <Toggle
            value={(() => {
              const sel = questionAnswers[qid];
              const opt = opts.find((o) => String(o.id) === String(sel));
              return opt ? /^yes/i.test(String(opt.optionValue || opt.optionLabel || "")) : false;
            })()}
            onChange={(v) => {
              const target = opts.find((o) => /^yes/i.test(String(o.optionValue || o.optionLabel || "")) === Boolean(v));
              if (target) setRadio(q, target.id);
            }}
            label={q.questionText}
          />
        )}

        {q.questionType === "CHECKBOX" && isChip(q) && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 14 }}>
            {opts.map((o, i) => {
              const cur = Array.isArray(questionAnswers[qid]) ? questionAnswers[qid] : [];
              const on = cur.includes(String(o.id));
              const desc = o.optionDescription || o.description || null;
              return (
                <div key={o.id} onClick={() => toggleCheckbox(q, o.id)} style={{
                  padding: "9px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
                  background: on ? BRAND_LIGHT : "#f5f5f3",
                  color: on ? BRAND_DARK : "#666",
                  border: `1px solid ${on ? BRAND : "transparent"}`,
                  transition: "all 0.15s",
                }}>
                  <span style={{ color: on ? BRAND : "#aaa", marginRight: 4, fontSize: 10 }}>{i + 1}.</span>{o.optionLabel}
                  {desc && (
                    <div style={{ fontSize: 10, color: "#888", marginTop: 3, fontWeight: 400, lineHeight: 1.4 }}>{desc}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {q.questionType === "CHECKBOX" && !isChip(q) && opts.map((o) => {
          const cur = Array.isArray(questionAnswers[qid]) ? questionAnswers[qid] : [];
          const on = cur.includes(String(o.id));
          return (
            <Toggle
              key={o.id}
              value={on}
              onChange={() => toggleCheckbox(q, o.id)}
              label={o.optionLabel}
              description={o.optionDescription || o.description || null}
            />
          );
        })}

        {q.questionType === "TEXTBOX" && (
          <TextInput
            value={questionAnswers[qid] || ""}
            onChange={(v) => setText(q, v)}
            placeholder={opts[0]?.optionLabel || ""}
          />
        )}
      </div>
    );
  };

  const ratedZip = paymentOrderDetails?.ratingResponse?.practicezipcode || "";
  const enteredZip = practiceZip || "";
  const zipPricingWillUpdate = ratedZip.length === 5 && enteredZip.length === 5 && ratedZip !== enteredZip;

  return (
    <>
      <QuoteStrip total={quoteStripAnnualTotal ?? quoteStripPremium} limits={snapshotLimits} claims={snapshotPolicyLine} amountPending={quoteStripAmountPending} />
      <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 19, fontWeight: 600, color: "#1a1a1a", margin: "6px 0 4px" }}>{lspGroup?.groupName || "License, scope & practice"}</h2>
      <p style={{ fontSize: 12, color: "#888", marginBottom: 14 }}>Your credentials and what you do day-to-day.</p>

      <PolicyHolderNameCard
        firstName={firstName}
        lastName={lastName}
        setFirstName={(v) => { personalStore.firstName = v; }}
        setLastName={(v) => { personalStore.lastName = v; }}
        isEditing={isEditingPolicyHolderName}
        onStartEditing={() => setIsEditingPolicyHolderName(true)}
        onStopEditing={() => { setIsEditingPolicyHolderName(false); saveNameEdit(); }}
        withRequired={withRequired}
      />
      <Field label="Date of birth">
        {/* Constrain the picker to real birth dates: no future dates (max =
            today, matching isDobAcceptable) and a sane lower bound. Manual
            typing is still validated by isDobAcceptable below. */}
        <DateInput
          value={dob}
          onChange={(v) => { personalStore.dob = formatDate(v); }}
          placeholder="MM/DD/YYYY"
          min="1900-01-01"
          max={new Date().toLocaleDateString("en-CA")}
        />
        {dob && !isDobAcceptable(dob) && (
          <div style={{ fontSize: 11, color: "#a44", marginTop: 4 }}>
            Enter a valid date of birth in MM/DD/YYYY format (not in the future).
          </div>
        )}
      </Field>
      <Field label={withRequired("Primary practice address")}><TextInput value={practiceAddress1} onChange={(v) => { personalStore.practiceAddress1 = v; }} placeholder="Address line 1" /></Field>
      <Field label="Address line 2" badge="optional"><TextInput value={practiceAddress2} onChange={(v) => { personalStore.practiceAddress2 = v; }} placeholder="Suite, apt, etc." /></Field>
      <div style={{ display: "flex", gap: 8 }}>
        <Field label={withRequired("City")} style={{ flex: 2 }}><TextInput value={practiceCity} onChange={(v) => { personalStore.practiceCity = v; }} placeholder="City" /></Field>
        <Field label={withRequired("State")} style={{ flex: 1 }}><TextInput value={practiceState} onChange={(v) => { personalStore.practiceState = v.replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase(); }} placeholder="PA" /></Field>
        <Field label={withRequired("ZIP")} style={{ flex: 1 }}><TextInput value={practiceZip} onChange={(v) => { const z = v.replace(/\D/g, "").slice(0, 5); if (z !== personalStore.practiceZip) { zipChangedRef.current = true; resetIlfDlfError(); signupStore.signupError = null; } personalStore.practiceZip = z; if (z.length >= 5) { fireZipUpdateRating(z); } }} placeholder="12345" inputMode="numeric" /></Field>
      </div>

      {zipPricingWillUpdate && (
        <Alert type="warning" className="alert-center">
        Pricing will be recalculated based on this ZIP code.
        </Alert>
      )}

      <div style={{ borderTop: "1px solid #f0f0f0", margin: "8px 0 12px" }} />

      <Field label="PA license number">
        <TextInput
          value={licenseNum}
          onChange={(v) => { personalStore.licenseNum = v.slice(0, 40); }}
          placeholder="State license #"
          maxLength={40}
        />
        {licenseNum && !isValidLicenseNumber(licenseNum.trim()) && (
          <div style={{ fontSize: 11, color: "#a44", marginTop: 4 }}>
            License number must be letters and digits only, up to 40 characters.
          </div>
        )}
      </Field>
      <Field label="NPI number" badge="optional">
        <TextInput
          value={npi}
          onChange={(v) => { personalStore.npi = v.replace(/\D/g, "").slice(0, 10); }}
          placeholder="10 digits"
          inputMode="numeric"
          maxLength={10}
        />
        {npi && !isValidNpi(npi) && (
          <div style={{ fontSize: 11, color: "#a44", marginTop: 4 }}>
            NPI must be exactly 10 digits.
          </div>
        )}
      </Field>

      <div style={{ borderTop: "1px solid #f0f0f0", margin: "8px 0 12px" }} />

      {submissionQuestionsLoading && <Loader label="Loading questions…" />}
      {submissionQuestionsError && (
        <Alert type="error">Couldn't load questions. {submissionQuestionsError.message}</Alert>
      )}

      {lspGroup && Array.isArray(lspGroup.questions) && sortedQuestions(lspGroup.questions).map(renderQuestion)}

      <Spacer />
      <Alert type="error" message={ilfDlfError?.message || signupError?.message || signupError} className="alert-center" />

      <button style={dis(btnPrimary, canAdvance() && !signupLoading)} onClick={handleContinue}>
        {signupLoading ? "Saving…" : "Continue"}
      </button>
    </>
  );
}

function PolicyHolderNameCard({
  firstName, lastName, setFirstName, setLastName,
  isEditing, onStartEditing, onStopEditing, withRequired,
}) {
  const displayName = `${firstName || ""} ${lastName || ""}`.trim() || "—";
  if (isEditing) {
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: "#888", fontFamily: "'DM Sans', sans-serif", textTransform: "uppercase", letterSpacing: 0.4 }}>Policy holder's name</span>
          <button type="button" onClick={onStopEditing} aria-label="Done editing name" style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", alignItems: "center" }}><CheckIcon /></button>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Field label={withRequired("First name")} style={{ flex: 1 }}>
            <TextInput value={firstName} onChange={setFirstName} placeholder="Jane" />
          </Field>
          <Field label={withRequired("Last name")} style={{ flex: 1 }}>
            <TextInput value={lastName} onChange={setLastName} placeholder="Doe" />
          </Field>
        </div>
      </div>
    );
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: "#888", fontFamily: "'DM Sans', sans-serif", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Policy holder's name</div>
      <div style={{ background: BRAND_LIGHT, borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <span style={{ fontFamily: "'Fraunces', serif", fontSize: 16, fontWeight: 500, color: BRAND_DARK }}>{displayName}</span>
        <button type="button" onClick={onStartEditing} aria-label="Edit policy holder's name" style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", alignItems: "center" }}><PencilIcon /></button>
      </div>
    </div>
  );
}
